import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import _ from 'lodash';
import React from 'react';

import { ColumnDef } from '@tanstack/react-table';
import { setHours, setMinutes } from 'date-fns';
import * as ImplEnum from '../../communs/enums/generic-enum';
import { FormInputProps } from '../../componets/form/formInterfaces';
import { IconComponent, icons } from '../../componets/icons';
import { IFormInputPropsFilter } from '../../componets/interfaces/formInputPropsFilter';
import { IFormSettinsProps } from '../../componets/interfaces/formProps';
import { ImperativeHandleAccessedByParentListComponent } from '../../componets/interfaces/listProps';
import ListComponet from '../../componets/list';
import { rowsPerPageOptions } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { setTypeUserInQuery } from '../../util/configTypeUser';
import {
    formatDateWithoutHours,
    momentZoneToDateAddDays,
    momentZoneToUnix,
} from '../../util/dateUtil';
import { formatNumber } from '../../util/infoFormat';

const defaultValues = {
    customer: {
        id: '',
        name: '',
    },
    user: [],
    situation: [],
    dateInitial: momentZoneToDateAddDays({ days: -90, startOfType: 'day' }),
    dateFinal: momentZoneToDateAddDays({ days: 10, endOfType: 'day' }),
    processNumber: '',
    code: '',
};

const enumInstances = {
    urgencyOptionsInstance: new ImplEnum.GenericEnum(ImplEnum.urgencyOptions),
    taskSituationOptionsInstance: new ImplEnum.GenericEnum(
        ImplEnum.taskSituationOptions,
    ),
    statusOptionsInstance: new ImplEnum.GenericEnum(ImplEnum.statusOptions),
    phaseLegalProcessOptionsInstance: new ImplEnum.GenericEnum(
        ImplEnum.phaseLegalProcessOptions,
    ),
    resultOptionsInstance: new ImplEnum.GenericEnum(ImplEnum.resultOptions),
};

const RenderList: React.FC<IFormSettinsProps> = props => {
    const { settings } = props;
    const { entity } = settings;
    const { user } = useAuth();

    const refForm =
        React.useRef<ImperativeHandleAccessedByParentListComponent>(null);

    const columnsTable = React.useMemo<ColumnDef<Object>[]>(
        () => [
            {
                id: 'code',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Código
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original?.code}
                        </TableCell>
                    );
                },
            },
            {
                id: 'select',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Número processo
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original?.processNumber}
                        </TableCell>
                    );
                },
            },
            {
                accessorKey: 'customer',
                cell: (cell: any) => {
                    return (
                        <TableCell
                            align="left"
                            onClick={() =>
                                refForm?.current?.handleClick(
                                    cell.row.original.id,
                                )
                            }>
                            {cell.row.original.customer.name}
                        </TableCell>
                    );
                },
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Cliente
                    </TableCell>
                ),
            },
            {
                id: 'user',
                header: (info: any) => {
                    return (
                        <TableCell align={'left'} padding={'normal'}>
                            Advogado
                        </TableCell>
                    );
                },
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original?.user?.name}
                        </TableCell>
                    );
                },
            },
            {
                id: 'situation',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Fase
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            key={row.id}
                            align="left"
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {
                                enumInstances.phaseLegalProcessOptionsInstance.getObject(
                                    row.original.phase,
                                )?.description
                            }
                        </TableCell>
                    );
                },
            },
            {
                accessorKey: 'createAt',
                cell: (cell: any) => (
                    <TableCell
                        align="left"
                        onClick={() =>
                            refForm?.current?.handleClick(cell.row.original.id)
                        }>
                        {formatDateWithoutHours(cell?.row.original?.createAt)}
                    </TableCell>
                ),
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Criado em
                    </TableCell>
                ),
            },
            {
                id: 'consultationValue',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Total em honorários
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original?.totalValue &&
                                formatNumber(row.original.totalValue)}
                        </TableCell>
                    );
                },
            },
            {
                accessorKey: 'deadlineDate',
                cell: (cell: any) => (
                    <TableCell
                        align="left"
                        onClick={() =>
                            refForm?.current?.handleClick(cell.row.original.id)
                        }>
                        {cell?.row.original?.deadlineDate &&
                            formatDateWithoutHours(
                                cell?.row.original?.deadlineDate,
                            )}
                    </TableCell>
                ),
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Prazo
                    </TableCell>
                ),
            },
            {
                id: 'diffInDays',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Dias faltantes
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {_.isNumber(row.original?.diffInDays) &&
                                row.original.diffInDays}
                        </TableCell>
                    );
                },
            },
            {
                id: 'situation',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Resultado
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            key={row.id}
                            align="left"
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original?.resultLegalProcess &&
                                enumInstances.resultOptionsInstance.getObject(
                                    row.original.resultLegalProcess,
                                )?.description}
                        </TableCell>
                    );
                },
            },
            {
                accessorKey: 'endDate',
                cell: (cell: any) => (
                    <TableCell
                        align="left"
                        onClick={() =>
                            refForm?.current?.handleClick(cell.row.original.id)
                        }>
                        {cell?.row.original.endDate
                            ? formatDateWithoutHours(
                                  cell?.row.original?.endDate,
                              )
                            : ''}
                    </TableCell>
                ),
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Finalizado em
                    </TableCell>
                ),
            },
            {
                id: 'select',
                header: ({ table }: any) => <TableCell align="center" />,
                cell: ({ row }: any) => (
                    <TableCell align="right">
                        <IconButton
                            onClick={() =>
                                refForm?.current?.handleEdit(row.original.id)
                            }>
                            <IconComponent icon={icons.ModeEdit} />
                        </IconButton>
                        <IconButton
                            onClick={() => {
                                refForm?.current?.setIdDelete(row.original.id);
                                refForm?.current?.setOpenModalDelete(true);
                            }}>
                            <IconComponent icon={icons.Delete} />
                        </IconButton>
                    </TableCell>
                ),
            },
        ],
        [],
    );

    const QUERY_DEFAULT = `page=1&limit=${
        rowsPerPageOptions[0]
    }&dateInitial=${momentZoneToUnix(
        defaultValues.dateInitial,
    )}&dateFinal=${momentZoneToUnix(defaultValues.dateFinal)}&user.id.array=${
        user.sub
    }&${entity}.status=01`;

    const inputsFilter = React.useMemo<IFormInputPropsFilter[]>(
        () => [
            {
                typeInput: 'text',
                name: `${entity}.code.equal`,
                control: null,
                label: 'Código',
                setValue: null,
                size: 'small',
                variant: 'standard',
                handleOnKeyPress: (data: any, name?: string) => {
                    if (data.charCode === 13 && name) {
                        refForm?.current?.handleByOnKeyPressEnterNewQuery(
                            data.target.value,
                            name,
                        );
                    }
                },
            },
            {
                typeInput: 'autocomplete',
                name: `${entity}.status`,
                variant: 'standard',
                control: null,
                label: 'Status',
                loadingAutocomplete: false,
                setValue: null,
                options: enumInstances.statusOptionsInstance.optionsList(),
                activeDebounce: false,
                handleChange: (event: any, option: any, name: string) => {
                    if (event.type === 'click' || event.type === 'keydown') {
                        refForm?.current?.handleByOnKeyPressEnterNewQuery(
                            option?.id,
                            name,
                        );
                    }
                },
            },
            {
                typeInput: 'autocomplete',
                name: `${entity}.urgency`,
                variant: 'standard',
                control: null,
                label: 'Urgência',
                loadingAutocomplete: false,
                setValue: null,
                options: enumInstances.urgencyOptionsInstance.optionsList(),
                activeDebounce: false,
                handleChange: (event: any, option: any, name: string) => {
                    if (event.type === 'click' || event.type === 'keydown') {
                        refForm?.current?.handleByOnKeyPressEnterNewQuery(
                            option?.id,
                            name,
                        );
                    }
                },
            },
            {
                typeInput: 'autocomplete',
                name: `${entity}.situation`,
                variant: 'standard',
                control: null,
                label: 'Situação',
                loadingAutocomplete: false,
                setValue: null,
                options:
                    enumInstances.taskSituationOptionsInstance.optionsList(),
                activeDebounce: false,
                handleChange: (event: any, option: any, name: string) => {
                    if (event.type === 'click' || event.type === 'keydown') {
                        refForm?.current?.handleByOnKeyPressEnterNewQuery(
                            option?.id,
                            name,
                        );
                    }
                },
            },
            {
                typeInput: 'autocomplete',
                name: `customer.id`,
                variant: 'standard',
                control: null,
                label: 'Cliente',
                loadingAutocomplete: false,
                setValue: null,
                handleChange: (event: any, option: any, name: string) => {
                    if (event.type === 'click' || event.type === 'keydown') {
                        refForm?.current?.handleByOnKeyPressEnterNewQuery(
                            option?.id,
                            name,
                        );
                    }
                },
                activeDebounce: true,
                query: (value: string) => `customer?name=${value}`,
            },
            {
                typeInput: 'text',
                name: `legalProcess.processNumber.ilike`,
                control: null,
                label: 'Número processo',
                setValue: null,
                size: 'small',
                variant: 'standard',
                handleOnKeyPress: (data: any, name?: string) => {
                    if (data.charCode === 13 && name) {
                        refForm?.current?.handleByOnKeyPressEnterNewQuery(
                            data.target.value,
                            name,
                        );
                    }
                },
            },
            {
                typeInput: 'multiple',
                name: `user.id.array`,
                variant: 'standard',
                control: null,
                label: 'Advogado',
                handleChange: (event: any, option: any[]) => {
                    if (event.type === 'click' || event.type === 'keydown') {
                        const data = option?.map(o => o.id);
                        refForm?.current?.handleByOnKeyPressEnterNewQuery(
                            data.length > 0 ? data : null,
                            'user.id.array',
                        );
                    }
                },
                setValue: null,
                entityPath: 'users',
                keyQuery: 'user.id',
                activeDebounce: true,
                query: (value: string) =>
                    `users?${setTypeUserInQuery(user.typeUser)}&limit=${value}`,
                DAODataList: (value: any) => {
                    return {
                        id: value.id,
                        description: value.name,
                    };
                },
            },
        ],
        [],
    );

    const mainInputsFilter = React.useMemo<FormInputProps[]>(
        () => [
            {
                typeInput: 'dateHour',
                name: 'dateInitial',
                control: null,
                label: 'Date de início',
                loadingAutocomplete: false,
                md: 6,
                xs: 12,
                handleChange: (data: any) => {
                    let newTime = setHours(data, 0);
                    newTime = setMinutes(newTime, 1);
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        momentZoneToUnix(newTime),
                        'dateInitial',
                    );
                },
                setValue: null,
                fullWidth: true,
                clearErrors: null,
                setError: null,
            },
            {
                typeInput: 'dateHour',
                name: 'dateFinal',
                control: null,
                label: 'Date de fim',
                loadingAutocomplete: false,
                md: 6,
                xs: 12,
                handleChange: (data: any) => {
                    let newTime = setHours(data, 23);
                    newTime = setMinutes(newTime, 59);
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        momentZoneToUnix(newTime),
                        'dateFinal',
                    );
                },
                setValue: null,
                fullWidth: true,
                clearErrors: null,
                setError: null,
            },
        ],
        [],
    );

    return (
        <ListComponet
            settings={settings}
            defaultValues={defaultValues}
            columnsTable={columnsTable}
            inputsFilter={inputsFilter}
            mainInputsFilter={mainInputsFilter}
            QUERY_DEFAULT={QUERY_DEFAULT}
            ref={refForm}
        />
    );
};

export default RenderList;
